import { z } from "zod";
import { validatePolygonSelection } from "./region";
import type { SceneRecipe } from "./types";

const centerSchema = z.object({
  lat: z.number().finite().min(-85.05112878).max(85.05112878),
  lng: z.number().finite().min(-180).max(180),
});

const selectionSchema = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("circle") }),
  z.object({
    mode: z.literal("polygon"),
    vertices: z.array(centerSchema).min(3).max(256),
  }).superRefine((selection, context) => {
    const validation = validatePolygonSelection(selection.vertices);
    for (const issue of validation.issues) {
      if (issue === "too-few-points" || issue === "too-many-points") continue;
      context.addIssue({
        code: "custom",
        path: ["vertices"],
        message: issue === "duplicate-points"
          ? "Custom boundary vertices must be unique."
          : issue === "self-intersection"
            ? "Custom boundary edges cannot cross."
            : "Custom boundary must enclose a non-zero area.",
      });
    }
  }),
]);

const layersSchema = z.object({
  imagery: z.boolean(),
  terrain: z.boolean(),
  landcover: z.boolean(),
  water: z.boolean(),
  roads: z.boolean(),
  underground: z.boolean(),
  barriers: z.boolean(),
  streetObjects: z.boolean(),
  buildings: z.boolean(),
  boundary: z.boolean(),
});

export const sceneRecipeSchema = z.object({
  schemaVersion: z.literal(2),
  center: centerSchema,
  radiusMeters: z.number().finite().min(50).max(1000),
  selection: selectionSchema,
  provider: z.literal("maptiler"),
  sources: z.object({
    imagery: z.literal("satellite-v2"),
    terrain: z.literal("terrain-rgb-v2"),
    buildings: z.literal("buildings"),
    vector: z.literal("v4"),
  }),
  quality: z.object({
    targetImageryMetersPerPixel: z.number().finite().min(0.1).max(5),
    maxTextureSize: z.union([z.literal(1024), z.literal(2048), z.literal(4096)]),
    maxRasterTiles: z.number().int().min(4).max(64),
  }),
  verticalScale: z.number().finite().min(0).max(10),
  camera: z.object({
    mode: z.enum(["perspective", "top"]),
    position: z.tuple([z.number().finite(), z.number().finite(), z.number().finite()]).optional(),
    target: z.tuple([z.number().finite(), z.number().finite(), z.number().finite()]).optional(),
  }),
  layers: layersSchema,
});

export const DEFAULT_SCENE_RECIPE: SceneRecipe = Object.freeze({
  schemaVersion: 2,
  center: { lat: 44.4268, lng: 26.1025 },
  radiusMeters: 250,
  selection: { mode: "circle" },
  provider: "maptiler",
  sources: {
    imagery: "satellite-v2",
    terrain: "terrain-rgb-v2",
    buildings: "buildings",
    vector: "v4",
  },
  quality: {
    targetImageryMetersPerPixel: 0.5,
    maxTextureSize: 4096,
    maxRasterTiles: 64,
  },
  verticalScale: 1,
  camera: { mode: "perspective" },
  layers: {
    imagery: true,
    terrain: true,
    landcover: true,
    water: true,
    roads: true,
    underground: false,
    barriers: true,
    streetObjects: true,
    buildings: true,
    boundary: true,
  },
} as SceneRecipe);

export function validateSceneRecipe(input: unknown): SceneRecipe {
  return sceneRecipeSchema.parse(input) as SceneRecipe;
}

/** A deliberately small migration surface for pre-release recipes. */
export function migrateSceneRecipe(input: unknown): SceneRecipe {
  if (!input || typeof input !== "object") {
    return validateSceneRecipe(input);
  }

  const candidate = input as Record<string, unknown>;
  if (candidate.schemaVersion === 2 || candidate.schemaVersion === 1) {
    const sources = candidate.sources && typeof candidate.sources === "object"
      ? candidate.sources as Record<string, unknown>
      : {};
    return validateSceneRecipe({
      ...candidate,
      schemaVersion: 2,
      selection: candidate.selection ?? DEFAULT_SCENE_RECIPE.selection,
      sources: { ...sources, vector: sources.vector ?? "v4" },
      layers: candidate.layers ?? DEFAULT_SCENE_RECIPE.layers,
    });
  }

  if (candidate.schemaVersion === 0 || candidate.schemaVersion === undefined) {
    return validateSceneRecipe({
      ...DEFAULT_SCENE_RECIPE,
      center: candidate.center ?? DEFAULT_SCENE_RECIPE.center,
      radiusMeters: candidate.radiusMeters ?? candidate.radius ?? DEFAULT_SCENE_RECIPE.radiusMeters,
      selection: candidate.selection ?? DEFAULT_SCENE_RECIPE.selection,
      verticalScale: candidate.verticalScale ?? DEFAULT_SCENE_RECIPE.verticalScale,
      camera: candidate.camera ?? DEFAULT_SCENE_RECIPE.camera,
      layers: candidate.layers ?? DEFAULT_SCENE_RECIPE.layers,
    });
  }

  return validateSceneRecipe(candidate);
}

/**
 * Reconstructing the object in schema order makes output deterministic and strips all unknown
 * fields, including accidental apiKey/token/session additions.
 */
export function serializeSceneRecipe(input: unknown): string {
  const recipe = migrateSceneRecipe(input);
  const safe: SceneRecipe = {
    schemaVersion: 2,
    center: { lat: recipe.center.lat, lng: recipe.center.lng },
    radiusMeters: recipe.radiusMeters,
    selection: recipe.selection.mode === "polygon"
      ? {
          mode: "polygon",
          vertices: recipe.selection.vertices.map((point) => ({ lat: point.lat, lng: point.lng })),
        }
      : { mode: "circle" },
    provider: "maptiler",
    sources: {
      imagery: "satellite-v2",
      terrain: "terrain-rgb-v2",
      buildings: "buildings",
      vector: "v4",
    },
    quality: {
      targetImageryMetersPerPixel: recipe.quality.targetImageryMetersPerPixel,
      maxTextureSize: recipe.quality.maxTextureSize,
      maxRasterTiles: recipe.quality.maxRasterTiles,
    },
    verticalScale: recipe.verticalScale,
    camera: {
      mode: recipe.camera.mode,
      ...(recipe.camera.position ? { position: [...recipe.camera.position] as [number, number, number] } : {}),
      ...(recipe.camera.target ? { target: [...recipe.camera.target] as [number, number, number] } : {}),
    },
    layers: { ...recipe.layers },
  };
  return `${JSON.stringify(safe, null, 2)}\n`;
}

export function redactSensitiveText(value: unknown): string {
  const text = value instanceof Error ? value.message : String(value);
  return text
    .replace(/([?&](?:key|api_key|token|access_token)=)[^&\s]+/gi, "$1[redacted]")
    .replace(/\b(?:AIza[\w-]{20,}|pk\.[\w.-]{20,})\b/g, "[redacted]");
}
